import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { onAuthStateChanged } from 'firebase/auth';
import { useAuth } from '../contexts/AuthContext';
import { useReorderMode } from '../contexts/ReorderModeContext';
import { auth } from '../config/firebase';
import api from '../services/api';
import '../styles/BottomNav.css';

// Routes where bottom nav should be hidden (pages with sticky headers)
const HIDDEN_NAV_ROUTES = [
  '/lists/new',
  '/add-place',
  '/edit',
  '/duplicate',
  '/place/save',
  '/place/add',
  '/place/',
  '/settings',
  '/admin',
  '/user/',
  '/legal/',
];

function BottomNav() {
  const { user } = useAuth();
  const { isReorderMode } = useReorderMode();
  const location = useLocation();

  // Check if current route should hide the nav
  const shouldHideNav = HIDDEN_NAV_ROUTES.some(route => location.pathname.includes(route));
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const [unreadActivityCount, setUnreadActivityCount] = useState(0);
  const [firebaseReady, setFirebaseReady] = useState(!!auth.currentUser);

  // Listen for Firebase auth state to know when we can make authenticated requests
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      setFirebaseReady(!!firebaseUser);
    });
    return () => unsubscribe();
  }, []);

  // Fetch notification counts using optimized single-read endpoint
  // This reads from denormalized counts on user doc instead of querying collections
  useEffect(() => {
    if (!user?.username || !firebaseReady) return;

    const fetchCounts = async () => {
      // Double-check Firebase auth is still ready
      if (!auth.currentUser) return;

      try {
        // Single API call that reads from user doc (1 Firestore read)
        // instead of 2 collection queries (N reads each)
        const response = await api.getBadgeCounts();
        const counts = response.data || {};
        setPendingRequestCount(counts.pendingFriendRequestCount || 0);
        setUnreadActivityCount(counts.unreadNotificationCount || 0);
      } catch (error) {
        // Silently fail - don't spam console for badge updates
      }
    };

    fetchCounts();

    // Refresh every 60 seconds (increased from 30s for cost savings)
    const interval = setInterval(fetchCounts, 60000);

    // Listen for custom event to refresh badge immediately
    const handleBadgeRefresh = () => fetchCounts();
    window.addEventListener('refreshNotificationBadge', handleBadgeRefresh);

    return () => {
      clearInterval(interval);
      window.removeEventListener('refreshNotificationBadge', handleBadgeRefresh);
    };
  }, [user?.username, firebaseReady]);

  const totalNotificationCount = pendingRequestCount + unreadActivityCount;

  const handleNavClick = (e) => {
    if (isReorderMode) {
      e.preventDefault();
    }
  };

  // Don't render nav on form pages
  if (shouldHideNav) {
    return null;
  }

  return (
    <nav className={`bottom-nav ${isReorderMode ? 'disabled' : ''}`} role="navigation" aria-label="Main navigation">
      <NavLink to="/lists" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'} aria-label="My Lists" onClick={handleNavClick}>
        <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <rect x="3" y="3" width="7" height="7" rx="1" />
          <rect x="14" y="3" width="7" height="7" rx="1" />
          <rect x="3" y="14" width="7" height="7" rx="1" />
          <rect x="14" y="14" width="7" height="7" rx="1" />
        </svg>
      </NavLink>

      <NavLink to="/following" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'} aria-label="Following" onClick={handleNavClick}>
        <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      </NavLink>

      <NavLink to="/discover" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'} aria-label="Discover" onClick={handleNavClick}>
        <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <circle cx="11" cy="11" r="8" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-4.35-4.35" />
        </svg>
      </NavLink>

      <NavLink to="/map" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'} aria-label="Map" onClick={handleNavClick}>
        <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
          <circle cx="12" cy="10" r="3" />
        </svg>
      </NavLink>

      <NavLink to="/notifications" className={({ isActive }) => isActive ? 'nav-item active' : 'nav-item'} aria-label={`Notifications${totalNotificationCount > 0 ? `, ${totalNotificationCount} unread` : ''}`} onClick={handleNavClick}>
        <div className="nav-icon-wrapper">
          <svg className="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
          {totalNotificationCount > 0 && (
            <span className="nav-badge" aria-live="polite" aria-atomic="true">
              <span className="sr-only">{totalNotificationCount} unread notifications</span>
              <span aria-hidden="true">{totalNotificationCount > 9 ? '9+' : totalNotificationCount}</span>
            </span>
          )}
        </div>
      </NavLink>
    </nav>
  );
}

export default BottomNav;
